import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Instagram, MessageCircle } from "lucide-react";
import { CONTACTS } from "@/data/content";
import { useLanguage, NAV_ITEMS } from "@/i18n";

export default function Footer() {
  const { t } = useLanguage();

  return (
    <footer data-testid="main-footer" className="border-t border-stone-300/80 bg-[#F0ECE4]">
      <div className="mx-auto max-w-7xl px-6 py-16 md:py-20">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-4">
              <img src="/logo-icon.png" alt="Edilcomplex2020" className="h-12 w-auto" />
              <div className="flex items-baseline gap-2">
                <span className="font-serif text-2xl font-semibold uppercase tracking-wide text-stone-900">
                  Edilcomplex
                </span>
                <span className="font-mono text-sm tracking-[0.2em] text-[#8F6B3C]">2020</span>
              </div>
            </div>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-stone-500">
              {t("footerTagline")}
            </p>
            <div className="mt-6 flex gap-3">
              <a
                data-testid="footer-instagram-link"
                href={CONTACTS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-stone-300 text-stone-600 transition-colors duration-300 hover:border-[#C89D66] hover:text-[#8F6B3C]"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                data-testid="footer-whatsapp-link"
                href={CONTACTS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-stone-300 text-stone-600 transition-colors duration-300 hover:border-[#C89D66] hover:text-[#8F6B3C]"
              >
                <MessageCircle className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#8F6B3C]">{t("footerNav")}</p>
            <ul className="mt-5 space-y-3">
              {NAV_ITEMS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    data-testid={`footer-${link.testid}-link`}
                    className="text-sm text-stone-500 transition-colors duration-300 hover:text-stone-900"
                  >
                    {t(link.key)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#8F6B3C]">{t("footerContacts")}</p>
            <ul className="mt-5 space-y-4 text-sm text-stone-500">
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#8F6B3C]" />
                <a href={CONTACTS.mapsLink} target="_blank" rel="noopener noreferrer" className="hover:text-stone-900">
                  {CONTACTS.address}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-4 w-4 shrink-0 text-[#8F6B3C]" />
                <a data-testid="footer-phone-link" href={CONTACTS.phoneHref} className="hover:text-stone-900">
                  {CONTACTS.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 shrink-0 text-[#8F6B3C]" />
                <a data-testid="footer-email-link" href={`mailto:${CONTACTS.email}`} className="hover:text-stone-900">
                  {CONTACTS.email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="h-4 w-4 shrink-0 text-[#8F6B3C]" />
                {CONTACTS.hours}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="overflow-hidden border-t border-stone-300/60">
        <p
          aria-hidden="true"
          className="whitespace-nowrap text-center font-serif text-[12vw] font-semibold uppercase leading-[0.85] tracking-tight text-stone-300/60 md:text-[8.5vw]"
        >
          Edilcomplex 2020
        </p>
      </div>

      <div className="border-t border-stone-300/60">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 px-6 py-5 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500 md:flex-row">
          <span>© {new Date().getFullYear()} Edilcomplex2020 S.r.l. · P.IVA 02558740466 — Capannori (LU)</span>
          <span className="flex flex-wrap items-center justify-center gap-5">
            <span className="hidden xl:inline">{t("footerSectors")}</span>
            <Link
              to="/privacy"
              data-testid="footer-privacy-link"
              className="transition-colors duration-300 hover:text-[#8F6B3C]"
            >
              Privacy Policy
            </Link>
            <Link
              to="/cookie-policy"
              data-testid="footer-cookie-link"
              className="transition-colors duration-300 hover:text-[#8F6B3C]"
            >
              Cookie Policy
            </Link>
            <Link
              to="/termini"
              data-testid="footer-termini-link"
              className="transition-colors duration-300 hover:text-[#8F6B3C]"
            >
              Termini e Condizioni
            </Link>
            <button
              data-testid="footer-cookie-prefs-btn"
              onClick={() => {
                localStorage.removeItem("edilcomplex-cookie-consent");
                window.location.reload();
              }}
              className="uppercase tracking-[0.2em] transition-colors duration-300 hover:text-[#8F6B3C]"
            >
              {t("cookiePrefs")}
            </button>
          </span>
        </div>
      </div>
    </footer>
  );
}
